/** You › Providers: provider accounts per machine, subscription usage, cost report, weekly stats. */
import { formatAgo } from '@viko/transcript-ui'
import { Laptop, Monitor } from 'lucide-react'
import { useEffect, useId, useMemo, useState } from 'react'
import { AccountUsageList, resetText, WindowGauge } from '../../components/AccountUsage.js'
import { BarkRouteDefault } from '../../components/BarkRouteDefault.js'
import { ProviderAccounts } from '../../components/ProviderAccounts.js'
import { ProvidersReport } from '../../components/ProvidersReport.js'
import { RoutingPreferences } from '../../components/RoutingPreferences.js'
import { UsageSettings } from '../../components/UsageSettings.js'
import { SkeletonRows } from '../../components/ui.js'
import { accountUsageRows } from '../../lib/account-usage.js'
import { resetCountdown } from '../../lib/format.js'
import { useMachines, useMetrics, useRuns, useUsage } from '../../lib/queries.js'
import type { UsageWindow } from '../../lib/types.js'
import { selectRowUsage } from '../../lib/usage-accounting.js'
import { Section } from './shared.js'

/**
 * Settings › Providers and usage (concept app-clarity `usage`): Subscriptions first (each
 * account's 5-hour and weekly windows), then spend this month per account, the provider accounts
 * on each machine, the cost-per-outcome report, routing, reset-usage settings and the week.
 */
export function ProvidersSection() {
  return (
    <>
      <Usage />
      <SpendThisMonth />
      <Accounts />
      <Section id="providers" title="Cost per outcome">
        <ProvidersReport />
      </Section>
      {/* ---- g11-routing: routing preferences ---- */}
      <Section id="routing" title="Routing">
        <RoutingPreferences />
        {/* ---- bark-route: global default route for barks ---- */}
        <BarkRouteDefault />
        {/* ---- end bark-route ---- */}
      </Section>
      {/* ---- end g11-routing ---- */}
      <UsageSettings />
      <Metrics />
    </>
  )
}

/** Provider accounts per machine: a quiet row each, never a card. */
function Accounts() {
  const machines = useMachines()
  return (
    <Section id="provider-accounts" title="Provider accounts">
      {machines.isPending ? <SkeletonRows n={1} /> : null}
      <ul className="st-rows">
        {(machines.data ?? []).map((m) => (
          <li key={m.id} className="st-row" aria-label={`${m.name} accounts`}>
            <span className="st-row__icon">
              {m.platform === 'mac' ? (
                <Laptop size={16} aria-hidden="true" />
              ) : (
                <Monitor size={16} aria-hidden="true" />
              )}
            </span>
            <span className="st-row__main">
              <h3 className="st-row__title m-0">{m.name}</h3>
              <span className="st-row__meta">
                <ProviderAccounts machine={m} />
              </span>
            </span>
            <span />
          </li>
        ))}
      </ul>
    </Section>
  )
}

const SUBS_NOTE = 'Viko routes away from a window at 90%'

function Usage() {
  const usage = useUsage()
  const machines = useMachines()
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(timer)
  }, [])
  // claude-accounts: per-account usage (Claude and Codex) from decrypted machine reports.
  const accountRows = accountUsageRows(machines.data ?? [], usage.data ?? [])
  if (accountRows.length)
    return (
      <Section
        id="usage"
        title="Subscriptions"
        count={`${accountRows.length} account${accountRows.length === 1 ? '' : 's'}`}
        note={SUBS_NOTE}
      >
        <AccountUsageList rows={accountRows} now={now} />
      </Section>
    )
  if (usage.isError) return null
  if (usage.isSuccess && !usage.data.length)
    return (
      <Section id="usage" title="Subscriptions">
        <p className="st-lede">
          Subscription meters appear after the first run reports its rate-limit windows.
        </p>
      </Section>
    )
  const groups = hubAccounts(usage.data ?? [])
  return (
    <Section
      id="usage"
      title="Subscriptions"
      count={`${groups.length} account${groups.length === 1 ? '' : 's'}`}
      note={SUBS_NOTE}
    >
      {usage.isPending ? <SkeletonRows n={2} /> : null}
      <ul className="st-subs" aria-label="Subscription usage">
        {groups.map((g) => (
          <HubAccount key={g.key} group={g} now={now} />
        ))}
      </ul>
    </Section>
  )
}

interface HubGroup {
  key: string
  name: string
  sub?: string
  agent?: string
  windows: { u: UsageWindow; label: string }[]
  observedAt?: number
  stale: boolean
}

/** Hub usage rows (`<account>:<window>`) grouped per account: "Claude Max · weekly" → Claude Max. */
export function hubAccounts(rows: readonly UsageWindow[]): HubGroup[] {
  const out = new Map<string, HubGroup>()
  for (const u of rows) {
    const key = u.accountId ?? u.id
    const cut = u.label.lastIndexOf(' · ')
    const name = cut > 0 ? u.label.slice(0, cut) : u.label
    const label = cut > 0 ? u.label.slice(cut + 3) : 'Usage'
    const m = name.match(/^(.*) \(([^)]+)\)$/)
    const g = out.get(key) ?? {
      key,
      name: m?.[1] ?? name,
      ...(m?.[2] ? { sub: m[2] } : {}),
      ...(u.agent ? { agent: String(u.agent) } : {}),
      windows: [],
      stale: false,
    }
    g.windows.push({ u, label: label.charAt(0).toUpperCase() + label.slice(1) })
    if (u.observedAt) g.observedAt = Math.max(g.observedAt ?? 0, u.observedAt)
    if (u.stale) g.stale = true
    out.set(key, g)
  }
  return [...out.values()]
}

function HubAccount({ group: g, now }: { group: HubGroup; now: number }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <li className="st-sub" aria-label={g.sub ? `${g.name} · ${g.sub}` : g.name}>
      <div className="st-sub__head">
        <span className="st-sub__name">
          {g.name}
          {g.sub ? <span className="st-sub__label"> · {g.sub}</span> : null}
        </span>
        <span className="flex-1" />
        <span className={`st-sub__src${g.stale ? ' st-wait' : ''}`}>
          from{' '}
          {g.agent === 'codex' ? 'OpenAI' : g.agent === 'claude' ? 'Anthropic' : 'the provider'}
          {g.observedAt ? ` · ${formatAgo(g.observedAt, now)}` : ''}
          {g.stale ? ' · stale' : ''}
        </span>
      </div>
      <div className="st-sub__windows">
        {g.windows.map(({ u, label }) => (
          <WindowGauge
            key={u.id}
            label={label}
            usedPct={u.usedPct}
            resetsAt={u.resetsAt}
            limit={!!u.limitReached}
            now={now}
            meterLabel={u.label}
            onDrill={() => setOpen((v) => !v)}
            drillOpen={open}
            drillId={id}
            sub={
              u.limitReached
                ? u.resetsAt > now
                  ? `exhausted · work resumes automatically ${resetCountdown(u.resetsAt, now)}${u.limitReached.stated ? '' : ' (reset not stated; rechecking)'}`
                  : 'Limit reset · resuming'
                : u.resetsAt > now
                  ? `${resetText(u.resetsAt, now)}${u.state === 'park' ? ' · parked' : ''}`
                  : 'Waiting for updated usage'
            }
          />
        ))}
      </div>
      {open ? (
        <div id={id} className="st-sub__details">
          {g.windows.map(({ u }) => (
            <p key={u.id} className="m-0">
              {u.label}: {u.limitReached ? 'exhausted' : `${Math.round(u.usedPct)}% used`}
              {u.resetsAt > now ? ` · Resets ${resetCountdown(u.resetsAt, now)}` : ''}
              {u.projectedWastePct !== undefined && !u.stale && u.resetsAt > now
                ? ` · ${Math.round(u.projectedWastePct)}% projected unused at reset`
                : ' · Projection unavailable'}
              {u.limitReached ? '. Flexible tasks run on the other agent meanwhile.' : ''}
            </p>
          ))}
        </div>
      ) : null}
    </li>
  )
}

/**
 * Spend this month, per account: what the provider CLIs reported plus what Viko estimated from
 * tokens (runs with neither are counted, never shown as free). Caps and balances are shown only
 * where a provider reports them.
 */
function SpendThisMonth() {
  const runs = useRuns()
  const rows = useMemo(() => monthSpend(runs.data ?? [], Date.now()), [runs.data])
  const month = new Date().toLocaleDateString([], { month: 'long' })
  if (!runs.isSuccess) return null
  return (
    <Section
      id="api-spend"
      title="Spend this month"
      count={month}
      note="Reported by the provider, or estimated by Viko"
    >
      {rows.length ? (
        <ul className="st-subs">
          {rows.map((r) => (
            <li key={r.account} className="st-sub" aria-label={`${r.account} spend`}>
              <div className="st-sub__head">
                <span className="st-sub__name">{r.label}</span>
                <span className="flex-1" />
                <span className="st-sub__src">
                  {r.runs} run{r.runs === 1 ? '' : 's'}
                  {r.missing ? ` · ${r.missing} without a cost` : ''}
                </span>
              </div>
              <div className="st-spend__line">
                <span>
                  <strong className="font-semibold">{usd(r.reported + r.estimated)}</strong> this
                  month
                </span>
                <span className="st-gauge__value">
                  {usd(r.reported)} reported · {usd(r.estimated)} estimated
                </span>
              </div>
              <p className="st-sub__empty">
                Hard cap and balance not reported by {r.agent === 'codex' ? 'OpenAI' : 'Anthropic'}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="st-lede">No run reported a cost this month.</p>
      )}
    </Section>
  )
}

const usd = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export function monthSpend(
  runs: readonly {
    agent?: string
    accountId?: string
    updatedAt?: number
    startedAt?: number
    costUsd?: number
    usageObservedAt?: number
    usageReport?: Parameters<typeof selectRowUsage>[0]['usageReport']
  }[],
  now: number,
) {
  const start = new Date(now)
  start.setDate(1)
  start.setHours(0, 0, 0, 0)
  const by = new Map<
    string,
    {
      account: string
      label: string
      agent: string
      reported: number
      estimated: number
      runs: number
      missing: number
    }
  >()
  for (const r of runs) {
    if (r.agent !== 'claude' && r.agent !== 'codex') continue
    const t = r.updatedAt ?? r.startedAt ?? 0
    if (t < start.getTime()) continue
    const account = r.accountId ?? r.agent
    const row = by.get(account) ?? {
      account,
      label: account === r.agent ? (r.agent === 'claude' ? 'Claude' : 'Codex') : account,
      agent: r.agent,
      reported: 0,
      estimated: 0,
      runs: 0,
      missing: 0,
    }
    const c = selectRowUsage(r).charge
    row.runs++
    if (c.kind === 'reported') row.reported += c.usd
    else if (c.kind === 'estimate') row.estimated += c.usd
    else row.missing++
    by.set(account, row)
  }
  return [...by.values()].sort((a, b) => b.reported + b.estimated - (a.reported + a.estimated))
}

function Metrics() {
  const m = useMetrics(7)
  if (!m.data) return null
  const d = m.data
  const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v)}%`)
  return (
    <Section id="metrics" title="This week">
      <dl className="stats st-pad">
        {d.resetUsage ? (
          <div>
            <dt>Tasks done using capacity before reset</dt>
            <dd>{d.resetUsage.tasksDone}</dd>
          </div>
        ) : null}
        <div>
          <dt>Tasks landed</dt>
          <dd>{d.progress.tasksLanded}</dd>
        </div>
        <div>
          <dt>Builds</dt>
          <dd>{d.progress.builds}</dd>
        </div>
        <div>
          <dt>Your inputs</dt>
          <dd>{d.attention.inputs}</dd>
        </div>
        <div>
          <dt>Tasks per input</dt>
          <dd>
            {d.efficiency.tasksPerInput == null ? '—' : d.efficiency.tasksPerInput.toFixed(1)}
          </dd>
        </div>
        <div>
          <dt>Machine utilization</dt>
          <dd>{pct(d.capacity.utilizationPct)}</dd>
        </div>
        <div>
          <dt>Idle slot-hours</dt>
          <dd>{Math.round(d.capacity.idleSlotHours)}</dd>
        </div>
        {d.corrections ? (
          <div>
            <dt>“I told you before”</dt>
            <dd className={d.corrections.onTarget === false ? 'text-status-wait' : ''}>
              {d.corrections.rate == null ? '—' : `${(d.corrections.rate * 100).toFixed(1)}%`}
              <span className="text-[14px] text-text-3">
                {' '}
                / &lt;{(d.corrections.target * 100).toFixed(1)}%
              </span>
            </dd>
          </div>
        ) : null}
      </dl>
    </Section>
  )
}
